import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { storedIdemKey, clearIdemKey, idemConfig, handleIdemConflict } from '../../lib/idempotency';
import axios from 'axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Check, Building2, Users, FileText, Truck, CheckCircle, Clock, Loader2, X, MinusCircle } from 'lucide-react';

// Constants from company/employee pages
import { COMPANY_DOCS } from '../companies/constants';
import { EMPLOYEE_DOCS } from '../employees/constants';

// Helper functions
import { formatDate, calculateOverallStatus } from '../../lib/helpers';

// Refactored wizard components
import WizardStep1 from './wizard/WizardStep1';
import WizardStep2, { withSgkReview } from './wizard/WizardStep2';
import WizardStep3Combined from './wizard/WizardStep3Combined';
import WizardStep1ReadOnly from './wizard/WizardStep1ReadOnly';
import { useLeaveDraftGuard, LeaveDraftDialog } from './wizard/LeaveDraftGuard';
import WizardStep4 from './wizard/WizardStep4';
import WizardStep5 from './wizard/WizardStep5';
import WizardSection, { flashElement } from './wizard/WizardSection';
import { Progress } from '../ui/progress';
import { getEquipmentDocs, permitEmployeeDocKeys } from './wizard/wizardHelpers';
import { confirmDialog } from '../ConfirmHost';
import { CommitmentGateDialog, fetchPendingCommitment } from '../isg/CommitmentGate';
import { blocksEmployeeDoc, blocksEquipmentDoc } from './wizard/BulkUploadDialog';
const SEAL_FIELDS = ['name', 'surname', 'tc_kimlik_no', 'position', 'nationality'];
import { snapshotCompany, snapshotList } from '../../lib/permitSnapshot';
import { unmatchedKeys, INFO_FIELDS } from './wizard/infoCorrections';
import { ValidityDialog } from './wizard/validityCheck';
import { useValidityPrompt } from './wizard/useValidityPrompt';
import { buildDeficiencyReport } from './wizard/deficiencyReport';
import { withFullTc } from '../../lib/tcPrivacy';
import { DeficiencyDialog, NewCompanyDialog, NewEmployeeDialog, OperatorSelectorDialog } from './wizard/WizardDialogs';
import { HazardClassPicker, HAZARD } from './wizard/HazardClassPicker';
import { useTimeRuleCheck, checkTimeRules } from './wizard/TimeRuleAlert';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WorkPermitWizard = ({ open, onClose, onSuccess, editMode = false, existingPermit = null, isFullPage = false }) => {
  const { isFirm, isReviewer } = useAuth();
  // İSG kaldığı bölümü sekme/oturum kapansa da hatırlar; firma sadece sayfa yenilemede
  const stepStore = isReviewer ? localStorage : sessionStorage;
  // Süreci tamamlanan talepte belgeler dondurulmuş haliyle gösterilir (firma eksik/hatalıyı düzeltirken canlı belgeleri görür)
  const snapPermit = editMode && existingPermit?.doc_snapshot && !(isFirm && existingPermit.status === 'eksik_hatali') ? existingPermit : null;
  const [currentStep, setCurrentStep] = useState(1);
  const [furthestStep, setFurthestStep] = useState(editMode && !isFirm ? 5 : 1);
  const [checkingStep, setCheckingStep] = useState(false);
  const [step1Ready, setStep1Ready] = useState(editMode);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [locations, setLocations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [workAreas, setWorkAreas] = useState([]);
  // Çalışma alanları yalnızca seçilen lokasyon için yüklenir (lokasyon başına bir kez)
  const [areaLocs, setAreaLocs] = useState({});
  const areaReq = useRef({});
  const loadAreas = useCallback((locId) => {
    if (!locId || areaReq.current[locId]) return;
    areaReq.current[locId] = true;
    axios.get(`${API}/work-areas`, { params: { location_id: locId } })
      .then(r => { setWorkAreas(prev => [...prev.filter(w => w.location_id !== locId), ...r.data]); setAreaLocs(p => ({ ...p, [locId]: true })); })
      .catch(() => { delete areaReq.current[locId]; toast.error('Çalışma alanları yüklenemedi'); });
  }, []);
  const [templates, setTemplates] = useState([]); // Eksiklik şablonları
  
  // Seçilen ekipmanlar (yeni dinamik yapı)
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  
  const [formData, setFormData] = useState({
    location_id: '',
    company_id: '',
    is_tipi: '',
    is_cesidi: '',
    work_area_id: '',
    job_description: '',
    start_date: '',
    end_date: '',
    start_time: '08:00',
    end_time: '18:00',
    employee_ids: [],
    employee_tasks: {},
    equipment_ids: [],
    // İSG Katip bilgileri
    isg_katip_uzman_name: '',
    isg_katip_hekim_name: '',
    sgk_sicil_last4: '',
    on_behalf_of: '',
    work_permit_types: [],
    special_situation_ids: [],
    special_situation_answers: {},
  });
  const [infoCorrections, setInfoCorrections] = useState({});
  
  const [selectedWorkArea, setSelectedWorkArea] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  
  // Edit mode - mevcut talep bilgileri
  const [permitId, setPermitId] = useState(null);
  const [requestNumber, setRequestNumber] = useState(null);
  const navigate = useNavigate();
  
  // Popover states
  const [openCompanyDocPopover, setOpenCompanyDocPopover] = useState(null);
  
  // Deficiency dialog states
  const [showDeficiencyDialog, setShowDeficiencyDialog] = useState(false);
  const [deficiencyContext, setDeficiencyContext] = useState({ type: '', docKey: '', target: null }); // type: 'company' or 'employee'
  const [selectedDeficiencies, setSelectedDeficiencies] = useState([]);
  
  // MYK listesi (Bölüm 3'e aktarılır)
  const [mykList, setMykList] = useState([]);
  
  // Operatör Belgesi selector states
  const [operatorList, setOperatorList] = useState([]);
  const [selectedOperatorObj, setSelectedOperatorObj] = useState(null);
  const [showOperatorSelector, setShowOperatorSelector] = useState(false);
  const [operatorSelectorForEquipment, setOperatorSelectorForEquipment] = useState(null);
  const [operatorSelectorStatus, setOperatorSelectorStatus] = useState(null); // 'onay' or 'eksik'
  const [operatorSearchQuery, setOperatorSearchQuery] = useState('');
  const [operatorValidityDate, setOperatorValidityDate] = useState('');
  
  // New company dialog
  const [newCompanyDialog, setNewCompanyDialog] = useState(false);
  const [newCompany, setNewCompany] = useState({ 
    name: '', 
    hazard_class: 'tehlikeli', 
    contact_name: '', 
    contact_surname: '', 
    contact_email: '', 
    contact_phone: '' 
  });
  
  // New employee dialog
  const [newEmployeeDialog, setNewEmployeeDialog] = useState(false);
  const [newEmployee, setNewEmployee] = useState({ name: '', surname: '', tc_kimlik_no: '', position: '' });
  
  
  // Dynamic equipment types from API
  const [equipmentTypes, setEquipmentTypes] = useState({});
  const [equipmentSubtypes, setEquipmentSubtypes] = useState([]);
  const [equipmentDialogOpen, setEquipmentDialogOpen] = useState(false);
  const [savingEquipment, setSavingEquipment] = useState(false);
  const [newEquipment, setNewEquipment] = useState({ name: '', equipment_type: '', subtype: '', documents: {} });

  const steps = [
    { number: 1, title: 'Temel Bilgiler', icon: FileText },
    { number: 2, title: 'Firma Belgeleri', icon: Building2 },
    { number: 3, title: 'Personel', icon: Users },
    { number: 4, title: 'İş Ekipmanı', icon: Truck },
    { number: 5, title: 'Özet & Onay', icon: CheckCircle },
  ];

  useEffect(() => {
    // Tam sayfa modunda veya dialog açıkken verileri yükle
    if (isFullPage || open) {
      fetchInitialData();
      if (!editMode || !existingPermit) {
        resetForm();
      }
    }
  }, [open, isFullPage]);

  // Edit mode - mevcut talep verilerini yükle
  useEffect(() => {
    if (editMode && existingPermit?.id && loadedPermitRef.current === existingPermit.id) stepStore.setItem(`wizard-step-${existingPermit.id}`, String(currentStep));
  }, [currentStep, editMode, existingPermit]);

  // Sadece bir kez yüklenir; firma listesi güncellenince (ör. belge onayı) form verileri sıfırlanmaz
  const loadedPermitRef = useRef(null);
  useEffect(() => {
    if (!isFullPage && !open) loadedPermitRef.current = null;
    if (editMode && existingPermit && (isFullPage || open) && !loading && companies.length > 0 && loadedPermitRef.current !== existingPermit.id) {
      loadedPermitRef.current = existingPermit.id;
      loadExistingPermitData();
    }
  }, [editMode, existingPermit, open, isFullPage, loading, companies]);

  const loadExistingPermitData = async () => {
    if (!existingPermit) return;
    
    // Form verilerini yükle
    setPermitId(existingPermit.id);
    setRequestNumber(existingPermit.request_number);
    behalfRef.current = existingPermit.behalf_company_id || '';
    eqCompanyRef.current = existingPermit.equipment_company_id || '';
    
    setFormData({
      location_id: existingPermit.location_id || '',
      company_id: existingPermit.company_id || '',
      is_tipi: existingPermit.is_tipi || '',
      is_cesidi: existingPermit.is_cesidi || '',
      work_area_id: existingPermit.work_area_id || '',
      job_description: existingPermit.job_description || '',
      start_date: existingPermit.start_date || '',
      end_date: existingPermit.end_date || '',
      start_time: existingPermit.start_time ?? '08:00',
      end_time: existingPermit.end_time ?? '18:00',
      employee_ids: existingPermit.employee_ids || [],
      employee_tasks: existingPermit.employee_tasks || {},
      equipment_ids: existingPermit.equipment_ids || [],
      isg_katip_uzman_name: existingPermit.isg_katip_uzman_name || '',
      isg_katip_hekim_name: existingPermit.isg_katip_hekim_name || '',
      sgk_sicil_last4: existingPermit.sgk_sicil_last4 || '',
      isg_katip_uzman_sicil_last4: existingPermit.isg_katip_uzman_sicil_last4 || '',
      isg_katip_hekim_sicil_last4: existingPermit.isg_katip_hekim_sicil_last4 || '',
      sgk_listed_employee_ids: existingPermit.sgk_listed_employee_ids ?? null,
      sgk_list_date: existingPermit.sgk_list_date || '',
      sgk_warning_ack_ids: existingPermit.sgk_warning_ack_ids || [],
      on_behalf_of: existingPermit.on_behalf_of || '',
      behalf_company_id: existingPermit.behalf_company_id || '',
      store_id: existingPermit.store_id || '',
      has_equipment: existingPermit.has_equipment ?? null,
      equipment_kinds: existingPermit.equipment_kinds || [],
      equipment_company_id: existingPermit.equipment_company_id || '',
      equipment_company_name: existingPermit.equipment_company_name || '',
      work_permit_types: existingPermit.work_permit_types || [],
      special_situation_ids: existingPermit.special_situation_ids || [],
      special_situation_answers: existingPermit.special_situation_answers || Object.fromEntries((existingPermit.special_situation_ids || []).map(id => [id, true])),
      special_situations: existingPermit.special_situations || [],
    });
    if (existingPermit.status === 'taslak' && existingPermit.wizard_step) {
      setFurthestStep(existingPermit.wizard_step);
      setCurrentStep(existingPermit.wizard_step);
    }
    // Sayfa yenilenirse (ör. önizleme yenilemesi) açık olan bölüme geri dön
    const savedStep = Number(stepStore.getItem(`wizard-step-${existingPermit.id}`) || sessionStorage.getItem(`wizard-step-${existingPermit.id}`));
    if (savedStep) {
      setCurrentStep(savedStep);
      setFurthestStep(prev => Math.max(prev, savedStep));
    }
    
    // Firma bilgilerini yükle
    if (existingPermit.company_id) {
      const company = companies.find(c => c.id === existingPermit.company_id);
      if (!behalfRef.current) setSelectedCompany(company || null);
      await fetchEmployees(existingPermit.company_id);
      await fetchEquipment(existingPermit.company_id);
    }
    
    // Seçili personelleri yükle
    if (existingPermit.employee_ids && existingPermit.employee_ids.length > 0) {
      // Personel listesi yüklendikten sonra seçili personelleri işaretleyeceğiz
      // Bu, employees state'i güncellendiğinde tetiklenecek
    }
  };

  useEffect(() => { loadAreas(formData.location_id); }, [formData.location_id, loadAreas]);

  useEffect(() => {
    if (formData.location_id && formData.is_tipi && formData.is_cesidi) {
      const area = workAreas.find(wa => 
        wa.location_id === formData.location_id && 
        wa.is_tipi === formData.is_tipi && 
        wa.is_cesidi === formData.is_cesidi
      );
      setSelectedWorkArea(area || null);
      if (area) {
        setFormData(prev => ({ ...prev, work_area_id: area.id }));
      }
    }
  }, [formData.location_id, formData.is_tipi, formData.is_cesidi, workAreas]);

  // Başka firma adına: belgeler ve personel alt firmadan; ana firma personeli görünür ama seçilemez
  const behalfRef = useRef(formData.behalf_company_id || '');
  useEffect(() => {
    if (!formData.company_id) return;
    const behalf = formData.behalf_company_id || '';
    if (behalfRef.current !== behalf) {
      setSelectedEmployees([]);
      setFormData(prev => ({ ...prev, employee_ids: [], employee_tasks: {} }));
    }
    behalfRef.current = behalf;
    const main = companies.find(c => c.id === formData.company_id);
    if (behalf) {
      axios.get(`${API}/companies/${behalf}`).then(r => setSelectedCompany({ ...r.data, parent_name: main?.name })).catch(() => toast.error('Adına talep açılan firma yüklenemedi'));
    } else {
      setSelectedCompany(main || null);
    }
    fetchEmployees(formData.company_id);
    fetchEquipment(formData.company_id);
  }, [formData.company_id, formData.behalf_company_id, companies]);

  // İş ekipmanı firması (A grubu): kendi belgeleri, personeli ve ekipmanı ayrı bölümde
  const eqCompanyRef = useRef(formData.equipment_company_id || '');
  const [equipmentCompany, setEquipmentCompany] = useState(null);
  useEffect(() => {
    const id = formData.equipment_company_id || '';
    const old = eqCompanyRef.current;
    if (old !== id && old) {
      const drop = (list) => list.filter(x => x.company_id !== old);
      setSelectedEmployees(prev => { const kept = drop(prev); setFormData(f => ({ ...f, employee_ids: kept.map(e => e.id) })); return kept; });
      setSelectedEquipment(prev => { const kept = drop(prev); setFormData(f => ({ ...f, equipment_ids: kept.map(e => e.id) })); return kept; });
    }
    eqCompanyRef.current = id;
    if (id) axios.get(`${API}/companies/${id}`).then(r => setEquipmentCompany(r.data)).catch(() => toast.error('Ekipman firması yüklenemedi'));
    else setEquipmentCompany(null);
    if (formData.company_id && old !== id) { fetchEmployees(formData.company_id); fetchEquipment(formData.company_id); }
  }, [formData.equipment_company_id]);
  const [newEmpTarget, setNewEmpTarget] = useState('');

  const resetForm = () => {
    setCurrentStep(1);
    setFurthestStep(editMode ? 5 : 1);
    setFormData({
      location_id: '',
      company_id: '',
      is_tipi: '',
      is_cesidi: '',
      work_area_id: '',
      store_id: '',
      job_description: '',
      start_date: '',
      end_date: '',
      start_time: '08:00',
      end_time: '18:00',
      employee_ids: [],
      employee_tasks: {},
      equipment_ids: [],
      isg_katip_uzman_name: '',
      isg_katip_hekim_name: '',
    sgk_sicil_last4: '',
    });
    setSelectedWorkArea(null);
    setSelectedCompany(null);
    setSelectedEmployees([]);
    setSelectedEquipment([]);
    setPermitId(null);
    setRequestNumber(null);
  };

  // Edit mode - employees yüklendiğinde seçili personelleri işaretle
  useEffect(() => {
    if (editMode && existingPermit && employees.length > 0 && formData.employee_ids.length > 0) {
      const selected = employees.filter(emp => formData.employee_ids.includes(emp.id));
      setSelectedEmployees(selected);
    }
  }, [editMode, existingPermit, employees.length, formData.employee_ids.length]);

  // employees değiştiğinde selectedEmployees'i güncelle (belge düzenlemesi sonrası)
  useEffect(() => {
    if (selectedEmployees.length > 0 && employees.length > 0) {
      // Mevcut seçili ID'leri koru, güncel employee verilerini kullan
      const selectedIds = selectedEmployees.map(e => e.id);
      const updatedSelected = employees.filter(emp => selectedIds.includes(emp.id));
      if (updatedSelected.length !== selectedEmployees.length || 
          JSON.stringify(updatedSelected.map(e => e.documents)) !== JSON.stringify(selectedEmployees.map(e => e.documents))) {
        setSelectedEmployees(updatedSelected);
      }
    }
  }, [employees]);

  // Edit mode - equipment yüklendiğinde seçili ekipmanları işaretle
  useEffect(() => {
    if (editMode && existingPermit && equipment.length > 0 && formData.equipment_ids.length > 0) {
      const selected = equipment.filter(eq => formData.equipment_ids.includes(eq.id));
      setSelectedEquipment(selected);
    }
  }, [editMode, existingPermit, equipment, formData.equipment_ids]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      if (isFirm) {
        const purge = await axios.post(`${API}/work-permits/purge-expired`).catch(() => null);
        if (purge?.data?.removed) toast.warning(`${purge.data.removed} belgenin geçerlilik süresi geçtiği için silindi. Lütfen güncel ve geçerli belgeyi yükleyin.`, { duration: 8000 });
      }
      const [locRes, compRes, templatesRes, mykRes, eqTypesRes, eqSubtypesRes, operatorRes] = await Promise.all([
        axios.get(`${API}/locations`),
        axios.get(`${API}/companies?page_size=500`),
        axios.get(`${API}/deficiency-templates`),
        axios.get(`${API}/myk-list`),
        axios.get(`${API}/equipment-types`),
        axios.get(`${API}/equipment-subtypes`),
        axios.get(`${API}/operator-list`),
      ]);
      setLocations(locRes.data);
      setCompanies((compRes.data.items || compRes.data).map(c => (snapPermit && c.id === snapPermit.company_id ? snapshotCompany(snapPermit, c) : c)));
      setTemplates(templatesRes.data);
      setMykList(mykRes.data);
      setEquipmentTypes(eqTypesRes.data);
      setEquipmentSubtypes(eqSubtypesRes.data);
      setOperatorList(operatorRes.data);
    } catch (error) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  // Talebe eklenen personelin kimlik bilgileri talepte mühürlüdür; çıkarılıp tekrar eklenince güncel bilgi gelir
  const sealedRef = useRef(null);
  if (sealedRef.current === null && (!editMode || existingPermit)) sealedRef.current = { ...(existingPermit?.employee_info || {}) };
  const rawEmpRef = useRef({});
  const applySeal = (list) => list.map(e => {
    const seal = sealedRef.current?.[e.id];
    if (!seal) return e;
    rawEmpRef.current[e.id] = Object.fromEntries(SEAL_FIELDS.map(f => [f, e[f]]));
    return { ...e, ...seal };
  });
  const unseal = (id) => {
    if (!sealedRef.current?.[id]) return null;
    delete sealedRef.current[id];
    const raw = rawEmpRef.current[id];
    if (raw) setEmployees(prev => prev.map(e => (e.id === id ? { ...e, ...raw } : e)));
    return raw;
  };

  const fetchEmployees = async (companyId) => {
    try {
      const behalf = behalfRef.current;
      const url = (cid) => `${API}/employees?company_id=${cid}&page_size=500&include_archived=true&permit_context=true&masked=true`;
      const res = await axios.get(url(behalf || companyId));
      let list = res.data.items || res.data;
      if (behalf && isFirm) {
        const main = await axios.get(url(companyId));
        list = [...list, ...(main.data.items || []).map(e => ({ ...e, _mainFirm: true }))];
      }
      if (eqCompanyRef.current) {
        const prov = await axios.get(url(eqCompanyRef.current));
        list = [...list, ...(prov.data.items || []).map(e => ({ ...e, _provider: true }))];
      }
      setEmployees(applySeal(snapPermit ? snapshotList(snapPermit, list, 'employees') : list));
    } catch (error) {
      console.error('Employees fetch error:', error);
    }
  };

  const fetchEquipment = async (companyId) => {
    try {
      const res = await axios.get(`${API}/equipment?company_id=${behalfRef.current || companyId}&page_size=500`);
      let list = res.data.items || res.data;
      if (eqCompanyRef.current) {
        const prov = await axios.get(`${API}/equipment?company_id=${eqCompanyRef.current}&page_size=500`);
        list = [...list, ...(prov.data.items || []).map(e => ({ ...e, _provider: true }))];
      }
      setEquipment(snapPermit ? snapshotList(snapPermit, list, 'equipment') : list);
    } catch (error) {
      console.error('Equipment fetch error:', error);
    }
  };

  const isTipiOptions = [...new Set(workAreas.filter(wa => wa.location_id === formData.location_id).map(wa => wa.is_tipi))];
  const isCesidiOptions = [...new Set(workAreas.filter(wa => wa.location_id === formData.location_id && wa.is_tipi === formData.is_tipi).map(wa => wa.is_cesidi))];

  const requiredEmployeeDocs = selectedWorkArea?.required_employee_docs || EMPLOYEE_DOCS.map(d => d.key);

  // Get templates for a document type
  const getTemplatesForDoc = (docKey) => {
    return templates.filter(t => t.document_type === docKey);
  };

  // Get templates for equipment based on subtype and document key
  const getTemplatesForEquipment = (equipmentType, equipmentSubtype, docKey) => {
    // Ekipman türüne göre şablonları getir (alt türden bağımsız, sabit belgeler)
    const prefix = equipmentType === 'arac' ? 'ekipman_arac_' : 'ekipman_is_';
    
    // Belge key'ini şablon formatına çevir
    let docSuffix = '';
    if (docKey === 'operatorluk_belgesi') docSuffix = 'operatorluk';
    else if (docKey === 'periyodik_muayene') docSuffix = 'periyodik';
    else if (docKey === 'mali_mesuliyet_sigortasi') docSuffix = 'mali';
    
    const fullDocType = `${prefix}${docSuffix}`;
    return templates.filter(t => t.document_type === fullDocType);
  };

  // Toggle deficiency checkbox
  const toggleDeficiency = (templateText) => {
    setSelectedDeficiencies(prev => 
      prev.includes(templateText) 
        ? prev.filter(t => t !== templateText)
        : [...prev, templateText]
    );
  };

  // Toggle equipment deficiency
  const toggleEquipmentDeficiency = (eqId, docKey, deficiencyText) => {
    setEquipment(prev => prev.map(eq => {
      if (eq.id !== eqId) return eq;
      const currentDeficiencies = eq.documents?.[`${docKey}_deficiencies`] || [];
      const newDeficiencies = currentDeficiencies.includes(deficiencyText)
        ? currentDeficiencies.filter(d => d !== deficiencyText)
        : [...currentDeficiencies, deficiencyText];
      return {
        ...eq,
        documents: {
          ...eq.documents,
          [`${docKey}_deficiencies`]: newDeficiencies
        }
      };
    }));
  };

  // Operatör Belgesi selector functions
  const openOperatorSelector = (equipment, status) => {
    setOperatorSelectorForEquipment(equipment);
    setOperatorSelectorStatus(status);
    setSelectedOperatorObj(null);
    setOperatorSearchQuery('');
    setOperatorValidityDate('');
    setShowOperatorSelector(true);
  };

  const handleOperatorSelection = async (operatorName, validityDate) => {
    if (!operatorSelectorForEquipment || !operatorName) return;
    if (!validityDate && operatorSelectorStatus === 'onay') {
      toast.error('Geçerlilik tarihi giriniz');
      return;
    }
    
    const eq = operatorSelectorForEquipment;
    const status = operatorSelectorStatus;
    
    try {
      const updatedDocs = { ...eq.documents };
      updatedDocs.operatorluk_belgesi = status;
      updatedDocs.operatorluk_belgesi_name = operatorName;
      
      if (status === 'eksik') {
        delete updatedDocs.operatorluk_belgesi_date;
        updatedDocs.operatorluk_belgesi_note = `Seçili belge: ${operatorName}`;
      } else if (status === 'onay') {
        updatedDocs.operatorluk_belgesi_date = validityDate;
        delete updatedDocs.operatorluk_belgesi_note;
      }
      
      await axios.put(`${API}/equipment/${eq.id}`, { ...eq, documents: updatedDocs });
      setEquipment(prev => prev.map(e => e.id === eq.id ? { ...e, documents: updatedDocs } : e));
      setSelectedEquipment(prev => prev.map(e => e.id === eq.id ? { ...e, documents: updatedDocs } : e));
      
      toast.success(`Operatörlük belgesi ${status === 'onay' ? 'onaylandı' : 'eksik olarak işaretlendi'}`);
      closeOperatorSelector();
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  const closeOperatorSelector = () => {
    setShowOperatorSelector(false);
    setOperatorSelectorForEquipment(null);
    setSelectedOperatorObj(null);
    setOperatorSearchQuery('');
    setOperatorValidityDate('');
  };

  // Open deficiency dialog when "eksik" is selected
  const openDeficiencyDialog = (type, docKey, target = null) => {
    setDeficiencyContext({ type, docKey, target });
    setSelectedDeficiencies([]);
    setShowDeficiencyDialog(true);
  };

  // Save deficiencies - Same logic as CompaniesPage
  const handleDeficiencySave = async () => {
    const deficiencyText = selectedDeficiencies.join(', ');
    
    if (deficiencyContext.type === 'company' && selectedCompany) {
      const updatedDocs = { ...selectedCompany.documents };
      updatedDocs[deficiencyContext.docKey] = 'eksik';
      updatedDocs[`${deficiencyContext.docKey}_note`] = deficiencyText;
      
      try {
        await axios.put(`${API}/companies/${selectedCompany.id}`, {
          ...selectedCompany,
          documents: updatedDocs
        });
        setSelectedCompany(prev => ({ ...prev, documents: updatedDocs }));
        setCompanies(prev => prev.map(c => c.id === selectedCompany.id ? { ...c, documents: updatedDocs } : c));
        toast.success('Eksiklik kaydedildi');
      } catch (error) {
        toast.error('Güncelleme başarısız');
      }
    } else if (deficiencyContext.type === 'employee' && deficiencyContext.target) {
      const employee = deficiencyContext.target;
      const updatedDocs = { ...employee.documents };
      updatedDocs[deficiencyContext.docKey] = 'eksik';
      updatedDocs[`${deficiencyContext.docKey}_note`] = deficiencyText;
      
      try {
        await axios.put(`${API}/employees/${employee.id}`, {
          ...employee,
          documents: updatedDocs
        });
        setEmployees(prev => prev.map(e => e.id === employee.id ? { ...e, documents: updatedDocs } : e));
        setSelectedEmployees(prev => prev.map(e => e.id === employee.id ? { ...e, documents: updatedDocs } : e));
        toast.success('Eksiklik kaydedildi');
      } catch (error) {
        toast.error('Güncelleme başarısız');
      }
    }
    
    setShowDeficiencyDialog(false);
    setSelectedDeficiencies([]);
  };

  // Company document update
  const handleCompanyDocUpdate = async (docKey, field, value, docConfig = null) => {
    if (!selectedCompany) return;
    
    // If selecting "eksik", open deficiency dialog
    if (field === 'status' && value === 'eksik') {
      openDeficiencyDialog('company', docKey);
      setOpenCompanyDocPopover(null);
      return;
    }
    
    // KURAL: Onay için tarih zorunluluğu (tarih gereken belgeler için)
    if (field === 'status' && value === 'onay') {
      const existingDate = selectedCompany.documents?.[`${docKey}_date`];
      // Check if this doc requires date (get from COMPANY_DOCS)
      const companyDocConfig = docConfig || COMPANY_DOCS.find(d => d.key === docKey);
      if (companyDocConfig?.hasDate && !existingDate) {
        toast.error('Onay vermek için önce tarih girmelisiniz!');
        return;
      }
    }
    
    const updatedDocs = { ...selectedCompany.documents };
    if (field === 'status') {
      updatedDocs[docKey] = value;
      // Onay durumunda notu temizle
      if (value === 'onay') {
        updatedDocs[`${docKey}_note`] = '';
      }
    } else {
      updatedDocs[`${docKey}_${field}`] = value;
    }
    
    try {
      await axios.put(`${API}/companies/${selectedCompany.id}`, {
        ...selectedCompany,
        documents: updatedDocs
      });
      
      // Update local state
      setSelectedCompany(prev => ({ ...prev, documents: updatedDocs }));
      setCompanies(prev => prev.map(c => c.id === selectedCompany.id ? { ...c, documents: updatedDocs } : c));
      toast.success('Belge güncellendi');
      setOpenCompanyDocPopover(null);
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  // Simple badge for company documents (Step 2)
  const getSimpleDocStatusBadge = (status) => {
    const statusConfig = {
      'onay': { color: 'bg-emerald-100 text-emerald-700', icon: <Check className="h-3.5 w-3.5" /> },
      'eksik': { color: 'bg-rose-100 text-rose-700', icon: <X className="h-3.5 w-3.5" /> },
      'beklemede': { color: 'bg-amber-100 text-amber-700', icon: <Clock className="h-3.5 w-3.5" /> },
      'kapsam_disi': { color: 'bg-slate-100 text-slate-500', icon: <MinusCircle className="h-3.5 w-3.5" /> },
    };
    const config = statusConfig[status] || statusConfig['beklemede'];
    return (
      <Badge className={`${config.color} inline-flex items-center justify-center w-7 h-6 p-0`}>
        {config.icon}
      </Badge>
    );
  };

  const handleEmployeeSelect = (employee, checked) => {
    if (checked && employee._mainFirm) return;
    if (checked) {
      const fresh = sealedRef.current?.[employee.id] ? employee : { ...employee, ...(rawEmpRef.current[employee.id] || {}) };
      setSelectedEmployees(prev => [...prev, fresh]);
      setFormData(prev => ({ ...prev, employee_ids: [...prev.employee_ids, employee.id] }));
    } else {
      unseal(employee.id);
      setSelectedEmployees(prev => prev.filter(e => e.id !== employee.id));
      setFormData(prev => ({ ...prev, employee_ids: prev.employee_ids.filter(id => id !== employee.id) }));
    }
  };

  // Kopyalanan talepte ekipman çıkarılınca o ekipmanın belgeleri kayıttan silinir (firma onaylarsa)
  const isCopiedForEq = isFirm && (existingPermit?.audit_log || []).some(a => a.action === 'copied');
  const confirmEquipmentRemoval = async (eqs, apply) => {
    const withDocs = eqs.filter(eq => Object.keys(eq?.document_files || {}).length);
    if (!isCopiedForEq || !withDocs.length) { apply(); return true; }
    const ok = await confirmDialog({
      title: 'Ekipman belgeleri silinecek',
      description: <>Bu ekipmanı talepten kaldırırsanız ekipmana bağlı belgeler (Periyodik Muayene, Mali Mesuliyet Sigortası vb.) <b>otomatik olarak silinir</b> ve ekipmanı tekrar eklerseniz belgeleri yeniden yüklemeniz gerekir. Firma ve personel belgeleri etkilenmez.
        <ul className="mt-2 list-disc pl-5 text-xs">{withDocs.map(eq => <li key={eq.id}><b>{eq.name}</b>: {Object.keys(eq.document_files).map(k => getEquipmentDocs(eq.equipment_type).find(d => d.key === k)?.label || k).join(', ')}</li>)}</ul></>,
      confirmLabel: 'Evet, Kaldır ve Belgeleri Sil', cancelLabel: 'Vazgeç',
    });
    if (!ok) return false;
    const cleared = {};
    for (const eq of withDocs) {
      for (const k of Object.keys(eq.document_files)) {
        try { await axios.delete(`${API}/equipment/${eq.id}/document-files/${k}`, { params: { approved: true } }); } catch (e) { toast.error(`${eq.name} – ${k} belgesi silinemedi`); }
      }
      cleared[eq.id] = true;
    }
    setEquipment(prev => prev.map(e => (cleared[e.id] ? { ...e, documents: {}, document_files: {} } : e)));
    apply();
    toast.success('Ekipman talepten kaldırıldı, belgeleri silindi');
    return true;
  };

  // Bölüm 1'de ekipman türü kaldırılınca o türdeki ekipmanlar da talepten çıkar
  const guardRemoveKinds = (keys, apply) => {
    const eqs = selectedEquipment.filter(e => keys.includes(e.equipment_subtype || e.subtype));
    if (!eqs.length) { apply(); return; }
    confirmEquipmentRemoval(eqs, () => { apply(); eqs.forEach(e => applyEquipmentSelect(e.id, false)); });
  };

  const handleEquipmentSelect = (equipmentId, checked) => {
    if (!checked && isCopiedForEq) {
      const eq = selectedEquipment.find(e => e.id === equipmentId);
      if (eq && Object.keys(eq.document_files || {}).length) return confirmEquipmentRemoval([eq], () => applyEquipmentSelect(equipmentId, false)).then(ok => ok && 'deleted');
    }
    applyEquipmentSelect(equipmentId, checked);
    return Promise.resolve(true);
  };
  const applyEquipmentSelect = (equipmentId, checked) => {
    if (checked) {
      setFormData(prev => ({ ...prev, equipment_ids: [...prev.equipment_ids, equipmentId] }));
      // Seçili ekipman listesini güncelle
      const eq = equipment.find(e => e.id === equipmentId);
      if (eq) {
        setSelectedEquipment(prev => [...prev, eq]);
      }
    } else {
      setFormData(prev => ({ ...prev, equipment_ids: prev.equipment_ids.filter(id => id !== equipmentId) }));
      setSelectedEquipment(prev => prev.filter(e => e.id !== equipmentId));
    }
  };

  // Yeni ekipman ekleme
  const handleAddEquipment = async () => {
    if (!newEquipment.name) { toast.error('Ekipman adı zorunludur'); return; }
    if (!newEquipment.equipment_type) { toast.error('Ekipman türü seçiniz'); return; }
    
    setSavingEquipment(true);
    try {
      const equipmentData = {
        name: newEquipment.name,
        equipment_type: newEquipment.equipment_type,
        equipment_subtype: newEquipment.subtype || '',
        company_id: newEquipment.equipment_type === 'arac' && formData.equipment_company_id ? formData.equipment_company_id : (formData.behalf_company_id || formData.company_id),
        location_id: formData.location_id,
        documents: newEquipment.documents || {}
      };
      
      const res = await axios.post(`${API}/equipment`, equipmentData);
      setEquipment(prev => [...prev, res.data]);
      // Otomatik olarak seç
      setFormData(prev => ({ ...prev, equipment_ids: [...prev.equipment_ids, res.data.id] }));
      setSelectedEquipment(prev => [...prev, res.data]);
      setEquipmentDialogOpen(false);
      setNewEquipment({ name: '', equipment_type: '', subtype: '', documents: {} });
      toast.success('Ekipman eklendi ve seçildi');
    } catch (error) {
      console.error('Ekipman ekleme hatası:', error);
      toast.error('Ekipman eklenemedi');
    } finally {
      setSavingEquipment(false);
    }
  };

  const handleAddCompany = async () => {
    if (!newCompany.name) { toast.error('Firma adı zorunludur'); return; }
    try {
      const res = await axios.post(`${API}/companies`, newCompany);
      setCompanies(prev => [...prev, res.data]);
      setFormData(prev => ({ ...prev, company_id: res.data.id }));
      setNewCompanyDialog(false);
      setNewCompany({ name: '', hazard_class: 'tehlikeli', contact_name: '', contact_surname: '', contact_email: '', contact_phone: '' });
      toast.success('Firma eklendi');
    } catch (error) {
      toast.error('Firma eklenemedi');
    }
  };

  const handleAddEmployee = async () => {
    if (!newEmployee.name || !newEmployee.surname) { toast.error('Ad ve soyad zorunludur'); return; }
    if (newEmployee.nationality === 'yabanci' && !newEmployee.tc_kimlik_no) { toast.error('Yabancı uyruklu personelde Pasaport No zorunludur'); return; }
    if (newEmployee.nationality !== 'yabanci' && newEmployee.tc_kimlik_no && !/^\d{11}$/.test(newEmployee.tc_kimlik_no)) { toast.error('T.C. Kimlik No 11 haneli olmalıdır'); return; }
    try {
      const res = await axios.post(`${API}/employees`, { ...newEmployee, company_id: newEmpTarget || formData.behalf_company_id || formData.company_id, on_behalf_of: newEmpTarget ? null : (formData.on_behalf_of?.trim() || null) });
      // Yeni eklenen personeli listenin başına ekle
      const added = newEmpTarget ? { ...res.data, _provider: true } : res.data;
      setEmployees(prev => [added, ...prev]);
      handleEmployeeSelect(added, true);
      setNewEmployeeDialog(false);
      setNewEmployee({ name: '', surname: '', tc_kimlik_no: '', position: '', nationality: 'tc' });
      toast.success('Personel eklendi ve talebe eklendi');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Personel eklenemedi, lütfen bilgileri kontrol edin');
    }
  };

  const handleSelectExistingEmployee = async (dup) => {
    let emp = employees.find(e => e.id === dup.id);
    if (!emp) {
      try {
        const r = await axios.get(`${API}/employees/${dup.id}`);
        emp = newEmpTarget ? { ...r.data, _provider: true } : r.data;
        setEmployees(prev => [emp, ...prev]);
      } catch { toast.error('Personel bilgisi alınamadı'); return; }
    }
    if (!formData.employee_ids.includes(emp.id)) handleEmployeeSelect(emp, true);
    setNewEmployeeDialog(false);
    setNewEmployee({ name: '', surname: '', tc_kimlik_no: '', position: '', nationality: 'tc' });
    toast.success(`${emp.name} ${emp.surname} talebe eklendi`);
  };

  const canProceed = (step = currentStep) => {
    switch (step) {
      case 1: 
        // Tarih validasyonu: bitiş tarihi başlangıçtan önce olamaz
        const datesValid = formData.start_date && formData.end_date && formData.end_date >= formData.start_date;
        return step1Ready && formData.location_id && formData.company_id && datesValid;
      case 3: return selectedEmployees.length > 0; // Personel seçimi
      default: return true;
    }
  };

  // Step 3'te "beklemede" durumundaki personelleri kontrol et
  const getPendingEmployees = () => {
    const docKeys = EMPLOYEE_DOCS.map(d => d.key);
    return selectedEmployees.filter(emp => {
      const overall = calculateOverallStatus(emp.documents, permitEmployeeDocKeys(emp, selectedWorkArea, docKeys, formData.selected_optional_employee_docs || []));
      return overall.status === 'beklemede';
    });
  };

  // İleri butonuna tıklandığında kontrol
  // Firma: zorunlu belge yüklenmeden veya eksik/hatalı belgenin yeni sürümü yüklenmeden ilerleyemez
  const getFirmBlockers = (steps, cells = []) => {
    const out = [];
    const check = (entity, doc, owner, required) => {
      const st = entity?.documents?.[doc.key];
      if (st === 'onay' || st === 'kapsam_disi') return;
      if (['eksik', 'suresi_doldu'].includes(st)) out.push(`${owner} – ${doc.label}: eksik/hatalı, yeni sürümü (eksiksiz halini) yükleyin`);
      else if (required && !entity?.document_files?.[doc.key]) out.push(`${owner} – ${doc.label}: belgeyi yükleyin`);
      else return;
      cells.push(`[data-testid="company-doc-row-${doc.key}"]`);
    };
    if (steps.includes(2) && selectedCompany) {
      COMPANY_DOCS.forEach(d => check(selectedCompany, d, 'Firma', true));
    }
    if (steps.includes(3)) {
      selectedEmployees.forEach(emp => EMPLOYEE_DOCS.forEach(d => {
        if (!blocksEmployeeDoc(emp, d.key, requiredEmployeeDocs)) return;
        const st = emp.documents?.[d.key];
        cells.push(`[data-testid="wizard-doc-${emp.id}-${d.key}-cell"]`);
        out.push(`${emp.name} ${emp.surname} – ${d.label}: ${['eksik', 'suresi_doldu'].includes(st) ? 'eksik/hatalı, yeni sürümü (eksiksiz halini) yükleyin' : 'belgeyi yükleyin'}`);
      }));
    }
    if (steps.includes(4)) {
      equipment.filter(eq => (formData.equipment_ids || []).includes(eq.id)).forEach(eq => getEquipmentDocs(eq.equipment_type).forEach(d => {
        if (!blocksEquipmentDoc(eq, d)) return;
        cells.push(`[data-testid="wizard-eq-doc-row-${eq.id}-${d.key}"]`);
        out.push(`${eq.name || 'Ekipman'} – ${d.label}: ${['eksik', 'suresi_doldu'].includes(eq.documents?.[d.key]) ? 'eksik/hatalı, yeni sürümü (eksiksiz halini) yükleyin' : 'belgeyi yükleyin'}`);
      }));
    }
    return out;
  };

  // Engelleyen tüm belge hücreleri kırmızı çerçeveyle yanıp söner (ilkine kaydırılır)
  const flashAll = (step, cells) => {
    const els = cells.map(c => document.querySelector(`#wizard-section-${step} ${c}`)).filter(Boolean);
    els.forEach((el, i) => (i === 0 ? flashElement(el) : (el.classList.remove('attention-blink'), void el.offsetWidth, el.classList.add('attention-blink'), setTimeout(() => el.classList.remove('attention-blink'), 1800))));
  };

  const warnFirmBlockers = (list) => toast.error('Devam etmeden önce şu belgeleri tamamlayın:', {
    duration: 10000,
    closeButton: true,
    description: (
      <ul className="mt-1 space-y-0.5 text-xs" data-testid="wizard-firm-blockers">
        {list.slice(0, 10).map((m, i) => <li key={i}>• {m}</li>)}
        {list.length > 10 && <li>… ve {list.length - 10} belge daha</li>}
      </ul>
    ),
  });

  const firmCorrections = isFirm && existingPermit?.status === 'talep_bilgisi_hatali' ? existingPermit.info_corrections : null;
  const pendingCorrections = unmatchedKeys(firmCorrections, formData);
  const warnCorrections = () => toast.error('Lütfen önce önerilen bilgiyi girin', {
    description: pendingCorrections.map(k => INFO_FIELDS[k].label).join(', '),
  });

  const deficiencyJumpRef = useRef(null);
  const [step3BlockSignal, setStep3BlockSignal] = useState(0);
  const [step4BlockSignal, setStep4BlockSignal] = useState(0);
  const { validityPrompt, validityBusy, askValidity, closeValidity, resetExpiredDocs, goToDates } = useValidityPrompt({
    isFirm, formData, selectedCompany, selectedEmployees, selectedEquipment, setSelectedCompany, setCompanies, setEmployees, setEquipment, setSelectedEquipment,
    openSection: (step) => openSection(step), onClose, deficiencyJumpRef,
    onAfterReset: (kinds) => jumpToFirstDeficiency(kinds),
  });

  // "Evet" sonrası: sırayla ilk eksik bölüme git (Firma > Personel > Ekipman)
  const jumpToFirstDeficiency = (kinds) => {
    const KIND_STEP = { company: 2, employee: 3, equipment: 4 };
    const targets = [...kinds.map(k => KIND_STEP[k]), ...[2, 3, 4].filter(s => getFirmBlockers([s]).length)];
    const step = Math.min(...targets);
    setCurrentStep(step);
    setFurthestStep(prev => Math.max(prev, step));
    setTimeout(() => document.getElementById(`wizard-section-${step}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
  };

  // İSG: onay bekleyen belgesi olan bölüm incelenmeden sonraki bölümlere geçilemez
  // Talebe özel tehlike sınıfı (İSG seçer, firma görmez)
  const [hazard, setHazard] = useState({});
  const hazardEnabled = isReviewer && !isFirm && editMode && !!permitId;
  const hazardEditable = hazardEnabled && ['isg_inceleme', 'talep_bilgisi_hatali'].includes(existingPermit?.status);
  useEffect(() => {
    if (!hazardEnabled) return;
    axios.get(`${API}/work-permits/${permitId}/hazard-class`).then(r => setHazard(r.data)).catch(() => {});
  }, [hazardEnabled, permitId, formData.equipment_company_id]);
  const hazardMissing = () => hazardEnabled && (!hazard.main?.value || (!!hazard.equipment && !hazard.equipment.value));
  const changeHazard = async (target, value) => {
    const h = hazard[target] || {};
    const base = h.value || h.suggestion;
    if (base && base !== value && !(await confirmDialog({
      title: 'Tehlike sınıfı değişiyor',
      description: `Önceki sınıf "${HAZARD[base]?.label}", yeni seçim "${HAZARD[value]?.label}". Bu talepteki ${target === 'equipment' ? 'ekipman firması ' : ''}personelin İSG Eğitimi ve Sağlık Raporu "Onay Bekliyor" durumuna düşecek ve geçerlilik tarihleri silinecek. Devam edilsin mi?`,
      confirmLabel: 'Evet, Değiştir',
    }))) return;
    try {
      const { data } = await axios.put(`${API}/work-permits/${permitId}/hazard-class`, { target, hazard_class: value });
      setHazard(prev => ({ ...prev, [target]: { value, suggestion: value } }));
      const resetIds = Object.keys(data.reset || {});
      if (resetIds.length) {
        const upd = (list) => list.map(e => {
          const keys = data.reset[e.id];
          if (!keys) return e;
          const docs = { ...(e.documents || {}) };
          keys.forEach(k => { docs[k] = 'beklemede'; delete docs[`${k}_validity`]; delete docs[`${k}_date`]; });
          return { ...e, documents: docs };
        });
        setEmployees(upd); setSelectedEmployees(upd);
        toast.warning(`${resetIds.length} personelin İSG Eğitimi ve Sağlık Raporu yeniden onaya düştü. Bölüm 3'te tarihleri girip onaylayın.`, { duration: 7000 });
      } else toast.success(`Tehlike sınıfı: ${HAZARD[value]?.label}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Tehlike sınıfı kaydedilemedi'); }
  };
  const hazardPicker = (target) => hazardEnabled && hazard[target] && (
    <HazardClassPicker testId={`hazard-picker-${target}`} title={target === 'equipment' ? 'İş Ekipmanı Firmasının Tehlike Sınıfı' : 'Firmanın Tehlike Sınıfı'}
      value={hazard[target].value} suggestion={hazard[target].suggestion} disabled={!hazardEditable} onChange={(v) => changeHazard(target, v)} />
  );

  const reviewerPendingBlock = (step) => {
    if (!isReviewer || ![2, 3, 4].includes(step)) return false;
    if (step === 2 && hazardMissing()) {
      toast.error('Tehlike sınıfı seçilmeden sonraki bölüme geçemezsiniz. Lütfen firmanın tehlike sınıfını seçin.', { duration: 6000 });
      flashElement(document.querySelector('[data-hazard-required="true"]'));
      return true;
    }
    const idPending = 0;
    const pendingN = idPending + (step === 2 ? reviewFirms().reduce((t, c) => t + pendingDocsIn(c), 0)
      : (step === 3 ? selectedEmployees : selectedEquipment).reduce((t, e) => t + pendingDocsIn(e), 0));
    if (!pendingN) return false;
    toast.error(`Bu bölümde onay bekleyen ${pendingN} belge var. Belgeleri inceleyip karar vermeden sonraki bölüme geçemezsiniz.`, { duration: 6000 });
    flashElement(document.querySelector(`#wizard-section-${step} [data-review-pending="true"]`) || document.querySelector(`#wizard-section-${step} [data-id-pending="true"]`));
    return true;
  };

  // Lokasyonun İSG taahhütnamesi: firma kullanıcısı güncel sürümü onaylamadıysa 1. bölümden ilerlemeden önce sorulur
  const [commitmentGate, setCommitmentGate] = useState(null);
  // Talep Zaman Kuralları (sadece firma): yeni talepte oluşturma anı + çalışma tarih/saati, mevcut taslakta sadece çalışma zamanı
  const timeCheck = useTimeRuleCheck(formData, isFirm && (!editMode || ['taslak', 'eksik_hatali', 'talep_bilgisi_hatali'].includes(existingPermit?.status)), !editMode || existingPermit?.status === 'taslak');
  const timeHits = timeCheck.hits;
  useEffect(() => {
    if (!isFirm || editMode || !(isFullPage || open)) return;
    checkTimeRules({ creation: true, work: false }).then(hits => {
      if (!hits.length) return;
      toast.error(hits.map(h => h.message).join(' '), { duration: 8000, id: 'time-rule-creation' });
      onClose?.();
    });
    axios.get(`${API}/firm/announcements`).then(r => {
      const block = r.data.filter(a => a.mode === 'ack' && a.block_permits);
      if (!block.length) return;
      toast.error(`Yeni talep oluşturmadan önce şu duyuruyu onaylamalısınız: ${block.map(a => a.title).join(', ')}`, { duration: 8000, id: 'ann-block' });
      window.dispatchEvent(new Event('announcements:refresh'));
      onClose?.();
    }).catch(() => {});
  }, [open, isFullPage]);
  const commitmentOk = useRef({});
  const handleNextStep = () => {
    if (currentStep === 1 && pendingCorrections.length) { warnCorrections(); return; }
    if (currentStep === 1 && timeHits.length) {
      toast.error(timeHits.map(h => h.message).join(' '), { duration: 7000 });
      const el = document.querySelector('[data-time-rule-block="true"]');
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      flashElement(el);
      return;
    }
    if (currentStep === 1 && isFirm && formData.location_id && !commitmentOk.current[formData.location_id]) {
      fetchPendingCommitment(formData.location_id).then(c => {
        if (c) { setCommitmentGate(c); return; }
        commitmentOk.current[formData.location_id] = true;
        handleNextStep();
      });
      return;
    }
    if (isFirm && [2, 3, 4].includes(currentStep)) {
      const cells = [];
      const blockers = getFirmBlockers([currentStep], cells);
      if (blockers.length) { warnFirmBlockers(blockers); setTimeout(() => flashAll(currentStep, cells), 500); if (currentStep === 3) setStep3BlockSignal(n => n + 1); if (currentStep === 4) setStep4BlockSignal(n => n + 1); return; }
    }
    if (reviewerPendingBlock(currentStep)) return;
    if ((currentStep === 2 && askValidity('company')) || (currentStep === 3 && askValidity('employee')) || (currentStep === 4 && askValidity('equipment'))) return;
    // Step 3'te (Personel) "beklemede" kontrolü yap (firma belgeleri incelemeye gönderir, kontrol İSG'de)
    if (currentStep === 3 && !isFirm) {
      const pendingEmps = getPendingEmployees();
      if (pendingEmps.length > 0) {
        const names = pendingEmps.map(e => `${e.name} ${e.surname}`).join(', ');
        toast.error(`${names} kişi${pendingEmps.length > 1 ? 'lerin' : 'nin'} genel belge statüsü "Beklemede" olduğu için lütfen gerekli belge kontrollerini yapın.`, {
          duration: 6000
        });
        return;
      }
    }
    setCheckingStep(true);
    setTimeout(() => {
      const next = currentStep + 1;
      setCheckingStep(false);
      setCurrentStep(next);
      setFurthestStep(prev => Math.max(prev, next));
      setTimeout(() => document.getElementById(`wizard-section-${next}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
      if (next === 2) askValidity('company');
      if (next === 3) askValidity('employee');
      if (next === 4) askValidity('equipment');
    }, 600);
  };

  // Konum belirlenene kadar bölümler gösterilmez; böylece doğrudan ilgili bölüm açık gelir (adım adım ilerleme görünmez)
  const [reviewPositioned, setReviewPositioned] = useState(!(editMode && (isReviewer || (isFirm && ['taslak', 'eksik_hatali', 'talep_bilgisi_hatali'].includes(existingPermit?.status)))));
  useEffect(() => {
    if (reviewPositioned) return undefined;
    const t = setTimeout(() => setReviewPositioned(true), 5000);
    return () => clearTimeout(t);
  }, [reviewPositioned]);

  // Firma eksik/hatalı talebi açınca eksik belgenin olduğu ilk bölüme atla
  useEffect(() => {
    if (!isFirm || !editMode || !['taslak', 'eksik_hatali', 'talep_bilgisi_hatali'].includes(existingPermit?.status) || deficiencyJumpRef.current === existingPermit.id) return;
    if (loadedPermitRef.current !== existingPermit.id || !selectedCompany) return;
    if (selectedEmployees.length < (formData.employee_ids || []).length || selectedEquipment.length < (formData.equipment_ids || []).length) return;
    deficiencyJumpRef.current = existingPermit.id;
    setReviewPositioned(true);
    // Her girişte geçerlilik uyarısı (kaldığı bölümden bağımsız)
    askValidity(['company', 'employee', 'equipment'], true);
    if (existingPermit.status !== 'eksik_hatali' || Number(stepStore.getItem(`wizard-step-${existingPermit.id}`)) > 1) return;
    const found = [2, 3, 4].map(s => ({ s, n: getFirmBlockers([s]).length })).filter(x => x.n > 0);
    if (!found.length) return;
    const step = found[0].s;
    setCurrentStep(step);
    setFurthestStep(prev => Math.max(prev, step));
    setTimeout(() => document.getElementById(`wizard-section-${step}`)?.scrollIntoView({ behavior: 'auto', block: 'start' }), 50);
    toast.info('Eksik belgeler', {
      duration: 8000,
      closeButton: true,
      description: <span data-testid="wizard-deficiency-jump-info">{found.map(x => `Bölüm ${x.s} – ${steps.find(st => st.number === x.s)?.title} (${x.n} belge)`).join(', ')}</span>,
    });
  }, [isFirm, editMode, existingPermit, selectedCompany, selectedEmployees, selectedEquipment, formData.employee_ids, formData.equipment_ids]);

  // İSG "İncele": onay bekleyen (yüklenmiş, karar verilmemiş) belgenin olduğu ilk bölüme atla
  const reviewJumpRef = useRef(null);
  useEffect(() => {
    if (!isReviewer || !editMode || reviewJumpRef.current === existingPermit?.id) return;
    if (loadedPermitRef.current !== existingPermit?.id || !selectedCompany) return;
    if (formData.equipment_company_id && equipmentCompany?.id !== formData.equipment_company_id) return;
    if (selectedEmployees.length < (formData.employee_ids || []).length || selectedEquipment.length < (formData.equipment_ids || []).length) return;
    reviewJumpRef.current = existingPermit.id;
    setReviewPositioned(true);
    const savedStep = Number(stepStore.getItem(`wizard-step-${existingPermit.id}`));
    const pendingIn = (entity) => Object.keys(entity?.document_files || {}).filter(k => (entity.documents?.[k] || 'beklemede') === 'beklemede').length;
    const found = [
      { s: 2, n: reviewFirms().reduce((t, c) => t + pendingIn(c), 0) },
      { s: 3, n: selectedEmployees.reduce((t, e) => t + pendingIn(e), 0) },
      { s: 4, n: selectedEquipment.reduce((t, e) => t + pendingIn(e), 0) },
    ].filter(x => x.n > 0);
    // Kaldığı bölüm hatırlanır; ancak öncesinde onay bekleyen belge varsa o bölüm atlanmaz
    if (savedStep > 1 && (!found.length || found[0].s >= savedStep)) return;
    // Onay bekleyen belge yoksa Bölüm 1 yerine Özet / Onay bölümü açılır
    if (!found.length) {
      setCurrentStep(5);
      setFurthestStep(5);
      setTimeout(() => document.getElementById('wizard-section-5')?.scrollIntoView({ behavior: 'auto', block: 'start' }), 50);
      return;
    }
    const step = found[0].s;
    setCurrentStep(step);
    setFurthestStep(prev => Math.max(prev, step));
    setTimeout(() => document.getElementById(`wizard-section-${step}`)?.scrollIntoView({ behavior: 'auto', block: 'start' }), 50);
    toast.info('Onay bekleyen belgeler', {
      duration: 8000,
      closeButton: true,
      description: <span data-testid="wizard-review-jump-info">{found.map(x => `Bölüm ${x.s} – ${steps.find(st => st.number === x.s)?.title} (${x.n} belge)`).join(', ')}</span>,
    });
  }, [isReviewer, editMode, existingPermit, selectedCompany, equipmentCompany, employees, selectedEmployees, selectedEquipment, formData.employee_ids, formData.equipment_ids, formData.equipment_company_id]);

  const openSection = (step) => {
    if (step > currentStep && isFirm) return;
    if (step > currentStep && reviewerPendingBlock(currentStep)) return;
    if (step > 2 && currentStep !== 2 && hazardMissing()) {
      setCurrentStep(2);
      setTimeout(() => reviewerPendingBlock(2), 300);
      return;
    }
    if (isFirm && currentStep === 1 && step !== 1 && pendingCorrections.length) { warnCorrections(); return; }
    setCurrentStep(step);
    if (isFirm) setFurthestStep(step);
    setTimeout(() => document.getElementById(`wizard-section-${step}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  };

  const pendingDocsIn = (entity) => Object.keys(entity?.document_files || {}).filter(k => (entity.documents?.[k] || 'beklemede') === 'beklemede').length;
  const permitEmpsOf = (secondary) => selectedEmployees.filter(e => (!!formData.equipment_company_id && e.company_id === formData.equipment_company_id) === secondary);
  const reviewFirms = () => [withSgkReview(selectedCompany, permitEmpsOf(false), isReviewer && !!permitId), equipmentCompany && withSgkReview(equipmentCompany, permitEmpsOf(true), isReviewer && !!permitId)].filter(Boolean);

  const getSectionState = (step) => {
    if (step === currentStep) return 'open';
    return step <= furthestStep ? 'done' : 'locked';
  };

  const getSectionSummary = (step) => {
    switch (step) {
      case 1: {
        const loc = locations.find(l => l.id === formData.location_id)?.name;
        const dates = formData.start_date ? `${formatDate(formData.start_date)} - ${formatDate(formData.end_date)}` : '';
        return [selectedCompany?.name && `Firma: ${selectedCompany.parent_name ? `${selectedCompany.parent_name} (${selectedCompany.name} adına)` : selectedCompany.name}`, loc, dates].filter(Boolean).join(' · ');
      }
      case 2: {
        const firms = reviewFirms();
        const total = COMPANY_DOCS.length * firms.length;
        const approved = firms.reduce((t, c) => t + COMPANY_DOCS.filter(d => c?.documents?.[d.key] === 'onay').length, 0);
        if (approved === total) return `${total}/${total} belge onaylandı`;
        const pending = firms.reduce((t, c) => t + COMPANY_DOCS.filter(d => c?.document_files?.[d.key] && c?.documents?.[d.key] !== 'onay').length, 0);
        return `${pending}/${total} belge onay bekliyor`;
      }
      case 3: return `${selectedEmployees.length} personel seçildi`;
      case 4: return selectedEquipment.length ? `${selectedEquipment.length} ekipman seçildi` : 'Ekipman seçilmedi';
      default: return '';
    }
  };

  // Yeni talep: işlem anahtarıyla tek sefer oluştur; kimlik hemen saklanır (sonraki adım hata verirse PUT ile devam)
  const IDEM_NAME = 'wp-create-idem';
  const createPermitOnce = async (payload) => {
    try {
      const res = await axios.post(`${API}/work-permits`, payload, idemConfig(storedIdemKey(IDEM_NAME)));
      clearIdemKey(IDEM_NAME);
      setPermitId(res.data.id);
      setRequestNumber(res.data.request_number);
      return res;
    } catch (error) {
      if (handleIdemConflict(error, navigate)) error.idemHandled = true;
      throw error;
    }
  };

  // Sayfa yenilendiyse: önceki denemede talep oluşmuşsa aynı talep üzerinden devam et
  useEffect(() => {
    if (editMode) return;
    const key = sessionStorage.getItem(IDEM_NAME);
    if (!key) return;
    axios.get(`${API}/work-permits/idempotency/${key}`).then(r => {
      const p = r.data.permit;
      if (!p) return;
      clearIdemKey(IDEM_NAME);
      toast.info(`Önceki denemenizde talep oluşturulmuştu (Talep No: ${p.request_number}). Aynı talepten devam ediyorsunuz.`, { duration: 8000 });
      navigate(`/work-permits/${p.id}/edit`, { replace: true });
    }).catch(() => {});
  }, []);

  // Eksiklik raporu gönder (PDF + Email)
  const handleSendDeficiencyReport = async () => {
    setSaving(true);
    try {
      // Rapor tam T.C. ile hazırlanır (listede yıldızlı gelir)
      const fullEmps = await withFullTc(selectedEmployees);
      const { companyDeficiencies, employeeDeficiencies, approvedEmployeeList, equipmentDeficiencies, approvedEquipmentList } = buildDeficiencyReport({ selectedCompany, selectedEmployees: fullEmps, selectedEquipment, formData, equipmentSubtypes });
      // Ekipman firması varsa PDF'te firmalar ayrı bölümler halinde listelenir
      const eqFirmId = equipmentCompany?.id;
      const section = (company, emps, eqs, isEq) => {
        const r = buildDeficiencyReport({ selectedCompany: company, selectedEmployees: emps, selectedEquipment: eqs, formData, equipmentSubtypes });
        return { company_id: company?.id, company_name: company?.name, is_equipment_firm: isEq, company_deficiencies: r.companyDeficiencies, employee_deficiencies: r.employeeDeficiencies, approved_employees: r.approvedEmployeeList, equipment_deficiencies: r.equipmentDeficiencies, approved_equipments: r.approvedEquipmentList };
      };
      const firmSections = eqFirmId ? [
        section(selectedCompany, fullEmps.filter(e => e.company_id !== eqFirmId), selectedEquipment.filter(e => e.company_id !== eqFirmId), false),
        section(equipmentCompany, fullEmps.filter(e => e.company_id === eqFirmId), selectedEquipment.filter(e => e.company_id === eqFirmId), true),
      ] : null;
      if (firmSections) companyDeficiencies.push(...firmSections[1].company_deficiencies.map(d => ({ ...d, document: `${d.document} (${equipmentCompany.name})` })));

      // Önce talebi oluştur veya güncelle
      const permitData = {
        ...formData,
        employee_ids: selectedEmployees.map(e => e.id),
        sealed_employee_ids: Object.keys(sealedRef.current || {}),
      };
      
      let permitResponse;
      if (permitId) {
        await axios.put(`${API}/work-permits/${permitId}`, permitData);
        permitResponse = { data: { id: permitId, request_number: requestNumber } };
      } else {
        permitResponse = await createPermitOnce(permitData);
      }
      
      // Eksiklik raporu gönder
      await axios.post(`${API}/work-permits/${permitResponse.data.id}/send-deficiency-report`, {
        company_name: selectedCompany?.name,
        company_email: selectedCompany?.contact_email,
        location_name: locations.find(l => l.id === formData.location_id)?.name,
        start_date: formData.start_date,
        end_date: formData.end_date,
        start_time: formData.start_time,
        end_time: formData.end_time,
        job_description: formData.job_description,
        is_tipi: formData.is_tipi,
        is_cesidi: formData.is_cesidi,
        company_deficiencies: companyDeficiencies,
        employee_deficiencies: employeeDeficiencies,
        approved_employees: approvedEmployeeList,
        equipment_deficiencies: equipmentDeficiencies,
        approved_equipments: approvedEquipmentList,
        isg_uzman_name: formData.isg_katip_uzman_name,
        isg_hekim_name: formData.isg_katip_hekim_name,
        firm_sections: firmSections,
        company_id: selectedCompany?.id,
        app_url: window.location.origin,
      });
      
      toast.success(`Eksiklik raporu firmaya gönderildi. Talep No: ${permitResponse.data.request_number}`);
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      if (error.idemHandled) return;
      toast.error(error.response?.data?.detail || 'Rapor gönderilemedi');
    } finally {
      setSaving(false);
    }
  };

  // Firma: talebi kaydet ve (isteğe bağlı) İSG incelemesine gönder
  const handleFirmSave = async (submit, leaveTo) => {
    if (submit && pendingCorrections.length) { warnCorrections(); return; }
    if (submit) {
      if (askValidity(['company', 'employee', 'equipment'])) return;
      const blockers = getFirmBlockers([2, 3, 4]);
      if (blockers.length) { warnFirmBlockers(blockers); if (getFirmBlockers([3]).length) setStep3BlockSignal(n => n + 1); if (getFirmBlockers([4]).length) setStep4BlockSignal(n => n + 1); return; }
    }
    setSaving(true);
    let id = permitId;
    const done = (msg) => { toast.success(msg); leaveGuard.disarm(); if (leaveTo) { leaveGuard.leave(leaveTo); return; } onSuccess && onSuccess(); onClose(); };
    try {
      const payload = { ...formData, wizard_step: furthestStep, sealed_employee_ids: Object.keys(sealedRef.current || {}) };
      if (id) {
        await axios.put(`${API}/work-permits/${id}`, payload);
      } else {
        const res = await createPermitOnce(payload);
        id = res.data.id;
      }
      if (submit) {
        await axios.put(`${API}/work-permits/${id}/submit-review`);
      }
      done(submit ? 'Talep İSG Uzmanı incelemesine gönderildi' : 'Talep taslak olarak kaydedildi');
    } catch (error) {
      if (error.idemHandled) return;
      // Önceki deneme sunucuda tamamlanmış ama yanıt ulaşmamışsa: talep zaten incelemede
      if (submit && id && error.response?.status === 400) {
        const cur = await axios.get(`${API}/work-permits/${id}`).then(r => r.data).catch(() => null);
        if (cur?.status === 'isg_inceleme') { done('Talep zaten İSG Uzmanı incelemesine gönderilmiş'); return; }
      }
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const leaveGuardActive = isFirm && !!formData.location_id && (!existingPermit || ['taslak', 'eksik_hatali', 'talep_bilgisi_hatali'].includes(existingPermit.status));
  const leaveGuard = useLeaveDraftGuard(isFirm && isFullPage, leaveGuardActive);

  const handleInfoError = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/work-permits/${permitId}/mark-info-error`, { corrections: infoCorrections });
      toast.success('Talep "Talep Bilgisi Hatalı" olarak firmaya gönderildi');
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = async () => {
    setSaving(true);
    try {
      let permitResponse;
      
      if (permitId) {
        await axios.put(`${API}/work-permits/${permitId}`, formData);
        permitResponse = { data: { id: permitId, request_number: requestNumber } };
      } else {
        permitResponse = await createPermitOnce(formData);
      }
      
      const finalRes = await axios.put(`${API}/work-permits/${permitResponse.data.id}/finalize`);
      if (finalRes.data.status === 'onay_bekliyor') {
        toast.success(`Talep 2. onaya gönderildi. Talep No: ${permitResponse.data.request_number}`);
      } else {
        toast.success(`Çalışma izni onaylandı. Talep No: ${permitResponse.data.request_number}`);
      }
      setReviewFinalized(true);
      await new Promise(r => setTimeout(r, 900));
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      if (error.idemHandled) return;
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  // Step 1: Basic Info (Refactored)
  const canReportInfo = !isFirm && existingPermit?.status === 'isg_inceleme';
  const renderStep1 = () => (!isFirm ? (
    <WizardStep1ReadOnly
      formData={formData} locations={locations} companies={companies} workAreas={workAreas} loadAreas={loadAreas}
      corrections={infoCorrections} setCorrections={canReportInfo ? setInfoCorrections : null} onGoToEnd={() => openSection(5)}
    />
  ) :
    <WizardStep1
      key={permitId || 'new'}
      corrections={firmCorrections}
      warnDocChange={isFirm && existingPermit?.status === 'eksik_hatali'}
      guardRemoveKinds={guardRemoveKinds}
      timeCheck={timeCheck}
      formData={formData}
      setFormData={setFormData}
      locations={locations}
      companies={companies}
      isTipiOptions={isTipiOptions}
      isCesidiOptions={isCesidiOptions}
      areasLoading={!!formData.location_id && !areaLocs[formData.location_id]}
      onGuideComplete={setStep1Ready}
      selectedWorkArea={selectedWorkArea}
      setNewCompanyDialog={setNewCompanyDialog}
    />
  );

  // Step 2: Company Documents (Refactored)
  const renderCompanyDocs = (company, apply, secondary) => (
    <WizardStep2
      secondary={secondary}
      onCompanyFieldSaved={(f, v) => apply(c => ({ ...c, documents: { ...(c.documents || {}), [f]: v } }))}
      permitId={permitId}
      permitEmployees={employees.filter(e => (formData.employee_ids || []).includes(e.id) && !!e._provider === secondary)}
      onSendDeficiency={editMode && permitId ? handleSendDeficiencyReport : undefined}
      onEmployeesRefresh={() => formData.company_id && fetchEmployees(formData.company_id)}
      formData={formData}
      setFormData={setFormData}
      selectedCompany={company}
      selectedWorkArea={selectedWorkArea}
      openCompanyDocPopover={openCompanyDocPopover}
      setOpenCompanyDocPopover={setOpenCompanyDocPopover}
      handleCompanyDocUpdate={handleCompanyDocUpdate}
      getSimpleDocStatusBadge={getSimpleDocStatusBadge}
      onCompanyFileUploaded={(docKey, info) => {
        const updateFn = (c) => ({ ...c, documents: { ...(c.documents || {}), [docKey]: 'beklemede' }, document_files: { ...(c.document_files || {}), [docKey]: info } });
        apply(updateFn);
      }}
      onCompanyFileDeleted={(docKey, res) => {
        const updateFn = (c) => {
          const files = { ...(c.document_files || {}) };
          if (res.file) files[docKey] = res.file; else delete files[docKey];
          return { ...c, documents: { ...(c.documents || {}), [docKey]: res.status }, document_files: files };
        };
        apply(updateFn);
      }}
      getTemplatesForDoc={getTemplatesForDoc}
      onCompanyDocDecided={(docKey, res) => {
        const updateFn = (c) => ({ ...c, documents: { ...(c.documents || {}), [docKey]: res.status, [`${docKey}_note`]: res.note, ...(res.status === 'onay' ? { [`${docKey}_validity`]: res.validity } : {}), ...Object.fromEntries((res.auto_approved || []).map(k => [k, 'onay'])), ...(res.sgk_memory || {}) } });
        apply(updateFn);
      }}
    />
  );

  const firmBanner = (label, name, eq, testId) => (
    <div className={`mb-4 flex items-center gap-3 rounded-lg px-4 py-3 text-white shadow-sm ${eq ? 'bg-red-600' : 'bg-blue-700'}`} data-testid={testId}>
      <Building2 className="h-5 w-5 shrink-0" />
      <span className="text-sm font-medium opacity-90">{label}:</span>
      <span className="text-base font-bold tracking-wide">{name}</span>
    </div>
  );

  const applyMainCompany = (fn) => { setSelectedCompany(prev => fn(prev)); setCompanies(prev => prev.map(c => c.id === selectedCompany.id ? fn(c) : c)); };
  const renderStep2 = () => (
    <>
      {equipmentCompany && firmBanner('Firma', selectedCompany?.name, false, 'main-firm-banner-docs')}
      {hazardPicker('main')}
      {renderCompanyDocs(selectedCompany, applyMainCompany, false)}
      {equipmentCompany && (
        <div className="mt-8 space-y-3 border-t-2 border-dashed border-slate-300 pt-6" data-testid="eq-company-docs">
          {firmBanner('İş Ekipmanı Firması', equipmentCompany.name, true, 'eq-firm-banner-docs')}
          {hazardPicker('equipment')}
          {renderCompanyDocs(equipmentCompany, (fn) => setEquipmentCompany(prev => fn(prev)), true)}
        </div>
      )}
    </>
  );

  // İSG eksiklik raporunda bildirilen personel belgeleri (emp.id:docKey)
  const reportedFixDocs = (existingPermit?.deficiency_report_data?.employee_deficiencies || []).flatMap(r => {
    const emp = selectedEmployees.find(e => `${e.name} ${e.surname}`.trim() === (r.name || '').trim());
    return emp ? (r.deficiencies || []).map(d => EMPLOYEE_DOCS.find(x => x.label === d.document)).filter(Boolean).map(x => `${emp.id}:${x.key}`) : [];
  });

  // Step 3: Employees (Refactored)
  // Step 3: Personel Seçimi ve Belge Düzenleme (Birleşik)
  const renderEmployeeSection = (list, company, target) => (
    <WizardStep3Combined
      sectionKey={target ? 'eq' : 'main'}
      setFormData={setFormData}
      isCopiedPermit={(existingPermit?.audit_log || []).some(a => a.action === 'copied')}
      fixMode={isFirm && existingPermit?.status === 'eksik_hatali'}
      permitId={permitId}
      reportedFixDocs={reportedFixDocs}
      blockSignal={step3BlockSignal}
      employees={list}
      setEmployees={setEmployees}
      selectedEmployees={selectedEmployees}
      setSelectedEmployees={setSelectedEmployees}
      selectedWorkArea={selectedWorkArea}
      selectedCompany={company}
      formData={formData}
      setNewEmployeeDialog={(v) => { setNewEmpTarget(target); setNewEmployeeDialog(v); }}
      sectionCompanyId={target || formData.behalf_company_id || formData.company_id}
      handleEmployeeSelect={handleEmployeeSelect}
      onEmployeesRefresh={() => fetchEmployees(formData.company_id)}
      templates={templates}
      mykList={mykList}
    />
  );

  const renderStep3 = () => (
    <>
      {equipmentCompany && firmBanner('Firma Personeli', selectedCompany?.name, false, 'main-firm-banner-employees')}
      {renderEmployeeSection(employees.filter(e => !e._provider), selectedCompany && { ...selectedCompany, hazard_class: hazard.main?.value || selectedCompany.hazard_class }, '')}
      {equipmentCompany && (
        <div className="mt-8 space-y-3 border-t-2 border-dashed border-slate-300 pt-6" data-testid="eq-company-employees">
          {firmBanner('İş Ekipmanı Firması Personeli', equipmentCompany.name, true, 'eq-firm-banner-employees')}
          {renderEmployeeSection(employees.filter(e => e._provider), { ...equipmentCompany, hazard_class: hazard.equipment?.value || equipmentCompany.hazard_class }, equipmentCompany.id)}
        </div>
      )}
    </>
  );

  // Step 4: Equipment
  const eqKindsSel = formData.equipment_kinds || [];
  const eqSubtypesForPermit = formData.has_equipment ? equipmentSubtypes.filter(st => eqKindsSel.includes(st.subtype_key)) : equipmentSubtypes;
  const eqTypesForPermit = formData.has_equipment ? Object.fromEntries(Object.entries(equipmentTypes || {}).filter(([k]) => eqSubtypesForPermit.some(st => st.main_type === k))) : equipmentTypes;
  const renderStep4 = () => formData.has_equipment === false ? (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-600" data-testid="wizard-no-equipment">
      Bu talepte iş ekipmanı yok. Bölüm 1'de "İş ekipmanı var mı?" sorusuna "Hayır" denildiği için bu bölüm atlanır.
    </div>
  ) : (
    <WizardStep4
      blockSignal={step4BlockSignal}
      equipment={equipment}
      setEquipment={setEquipment}
      selectedEquipment={selectedEquipment}
      setSelectedEquipment={setSelectedEquipment}
      formData={formData}
      equipmentTypes={eqTypesForPermit}
      equipmentSubtypes={eqSubtypesForPermit}
      equipmentDialogOpen={equipmentDialogOpen}
      setEquipmentDialogOpen={setEquipmentDialogOpen}
      newEquipment={newEquipment}
      setNewEquipment={setNewEquipment}
      savingEquipment={savingEquipment}
      handleEquipmentSelect={handleEquipmentSelect}
      handleAddEquipment={handleAddEquipment}
      getTemplatesForEquipment={getTemplatesForEquipment}
      toggleEquipmentDeficiency={toggleEquipmentDeficiency}
      openOperatorSelector={openOperatorSelector}
      operatorList={operatorList}
    />
  );

  // Step 5: Summary
  const renderStep5 = () => (
    <WizardStep5
      formData={formData}
      selectedWorkArea={selectedWorkArea}
      locations={locations}
      selectedCompany={selectedCompany}
      selectedEmployees={selectedEmployees}
      selectedEquipments={selectedEquipment}
      saving={saving}
      handleSendDeficiencyReport={handleSendDeficiencyReport}
      handleFinalize={handleFinalize}
      isFirm={isFirm}
      handleFirmSave={handleFirmSave}
      equipmentSubtypes={equipmentSubtypes}
      infoCorrections={infoCorrections}
      handleInfoError={handleInfoError}
      onEditInfo={() => openSection(1)}
      firmInfoBlocked={pendingCorrections.length > 0}
      equipmentCompany={equipmentCompany}
    />
  );

  // İSG: yüklenmiş belgelerden kaçına karar verildi (onay / eksik / kapsam dışı)
  const [reviewFinalized, setReviewFinalized] = useState(false);
  // PDF'teki "Belgeye git" bağlantısı: ?focus=tür:id:belge → ilgili bölüm açılır, satır yanıp söner
  const focusDoneRef = useRef(false);
  useEffect(() => {
    const focus = new URLSearchParams(window.location.search).get('focus');
    if (!focus || focusDoneRef.current || !existingPermit || !selectedCompany) return;
    const [kind, id, key] = focus.split(':');
    const step = { company: 2, employee: 3, equipment: 4 }[kind];
    if (!step) return;
    if (kind === 'employee' && !selectedEmployees.some(e => e.id === id)) return;
    if (kind === 'equipment' && !selectedEquipment.some(e => e.id === id)) return;
    focusDoneRef.current = true;
    setFurthestStep(s => Math.max(s, step));
    setCurrentStep(step);
    setTimeout(() => {
      const sel = kind === 'company' ? `[data-testid="company-doc-row-${key}"]` : `[data-testid="wizard-${kind === 'employee' ? 'emp' : 'eq'}-row-${id}"]`;
      const el = document.querySelector(`#wizard-section-${step} ${sel}`) || document.querySelector(`#wizard-section-${step}`);
      flashElement(el);
    }, 900);
  }, [existingPermit, selectedCompany, selectedEmployees, selectedEquipment]);
  const reviewCounts = () => {
    const all = [...reviewFirms(), ...selectedEmployees, ...selectedEquipment].flatMap(e => Object.keys(e?.document_files || {}).map(k => e.documents?.[k] || 'beklemede'));
    return { total: all.length, done: all.filter(st => st !== 'beklemede').length };
  };
  const { done: progDone, total: progTotal } = isReviewer && editMode ? reviewCounts() : { done: 0, total: 0 };
  // Talep listesindeki mini ilerleme için sunucuya bildir
  useEffect(() => {
    if (!isReviewer || !permitId || existingPermit?.status !== 'isg_inceleme' || !reviewPositioned) return undefined;
    const t = setTimeout(() => axios.put(`${API}/work-permits/${permitId}/review-presence`, { active: true, done: progDone, total: progTotal }).catch(() => {}), 800);
    return () => clearTimeout(t);
  }, [progDone, progTotal, reviewPositioned]);

  const reviewProgressBar = () => {
    const { total, done } = { total: progTotal, done: progDone };
    if (!total) return null;
    // Belgeler bitince %98; "Talebi Onayla" ile %100
    const pct = reviewFinalized ? 100 : Math.round((done / total) * 98);
    return (
      <div className="sticky top-[57px] z-[6] rounded-xl border bg-white/95 px-4 py-3 shadow-sm backdrop-blur" data-testid="review-progress">
        <div className="mb-1.5 flex items-center justify-between text-sm">
          <span className="font-semibold text-slate-800" data-testid="review-progress-text">İnceleme ilerlemesi: {done} / {total} belge incelendi</span>
          <span className={`text-xs font-semibold ${pct === 100 ? 'text-emerald-700' : 'text-amber-700'}`}>{reviewFinalized ? 'Talep onaylandı' : done >= total ? 'Tüm belgeler incelendi – "Talebi Onayla" ile tamamlayın' : `${total - done} belge onay bekliyor`}</span>
        </div>
        <Progress value={pct} className={`h-2.5 ${pct === 100 ? '[&>div]:bg-emerald-600' : '[&>div]:bg-amber-500'}`} />
      </div>
    );
  };

  // Wizard içeriği - tek sayfa akordiyon
  const stepRenderers = { 1: renderStep1, 2: renderStep2, 3: renderStep3, 4: renderStep4, 5: renderStep5 };
  const wizardContent = loading ? (
    <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
  ) : (
    <div className="space-y-4" data-testid="wizard-accordion">
      {isReviewer && editMode && reviewProgressBar()}
      {steps.map(step => (
        <WizardSection
          key={step.number}
          step={step}
          state={getSectionState(step.number)}
          summary={getSectionSummary(step.number)}
          onEdit={() => openSection(step.number)}
          onContinue={step.number < 5 ? handleNextStep : null}
          canContinue={canProceed(step.number)}
          checking={checkingStep && currentStep === step.number}
          blockedHint={step.number === 3 ? 'Devam etmek için en az bir personel seçin' : undefined}
          continueLabel={step.number === 4 ? 'Özete geç' : `Sonraki bölüm: ${steps[step.number]?.title || ''}`}
        >
          {getSectionState(step.number) === 'open' && stepRenderers[step.number]()}
        </WizardSection>
      ))}
      <div className="flex justify-end pt-2">
        <Button type="button" variant="ghost" onClick={onClose} data-leave-target="/work-permits" data-testid="wizard-cancel-btn">İptal</Button>
      </div>
    </div>
  );

  // Uygulama yalnızca tam sayfa modunu kullanır (WorkPermitWizardPage)
  return (
    <>
      <ValidityDialog prompt={validityPrompt} onYes={resetExpiredDocs} onNo={goToDates} onClose={closeValidity} busy={validityBusy} />
      <LeaveDraftDialog
        open={!!leaveGuard.target}
        saving={saving}
        onSave={() => handleFirmSave(false, leaveGuard.target)}
        onDiscard={() => leaveGuard.leave()}
        onCancel={leaveGuard.cancel}
      />
      <DeficiencyDialog
        showDeficiencyDialog={showDeficiencyDialog} setShowDeficiencyDialog={setShowDeficiencyDialog} deficiencyContext={deficiencyContext}
        selectedDeficiencies={selectedDeficiencies} setSelectedDeficiencies={setSelectedDeficiencies}
        getTemplatesForDoc={getTemplatesForDoc} toggleDeficiency={toggleDeficiency} handleDeficiencySave={handleDeficiencySave}
      />

      {reviewPositioned ? wizardContent : (
        <div className="flex items-center justify-center py-24 text-slate-500" data-testid="wizard-review-positioning">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />Talep yükleniyor...
        </div>
      )}
      
      <NewCompanyDialog newCompanyDialog={newCompanyDialog} setNewCompanyDialog={setNewCompanyDialog} newCompany={newCompany} setNewCompany={setNewCompany} handleAddCompany={handleAddCompany} />

      {commitmentGate && (
        <CommitmentGateDialog commitment={commitmentGate} permitId={permitId} onClose={() => setCommitmentGate(null)}
          onAccepted={() => { commitmentOk.current[formData.location_id] = true; setCommitmentGate(null); handleNextStep(); }} />
      )}
      <NewEmployeeDialog newEmployeeDialog={newEmployeeDialog} setNewEmployeeDialog={setNewEmployeeDialog} newEmployee={newEmployee} setNewEmployee={setNewEmployee} handleAddEmployee={handleAddEmployee} onBehalfOf={formData.on_behalf_of}
        companyId={newEmpTarget || formData.behalf_company_id || formData.company_id} selectedIds={formData.employee_ids} onSelectExisting={handleSelectExistingEmployee} />

      <OperatorSelectorDialog
        showOperatorSelector={showOperatorSelector} closeOperatorSelector={closeOperatorSelector} operatorSelectorStatus={operatorSelectorStatus}
        operatorSelectorForEquipment={operatorSelectorForEquipment} operatorSearchQuery={operatorSearchQuery} setOperatorSearchQuery={setOperatorSearchQuery}
        operatorList={operatorList} selectedOperatorObj={selectedOperatorObj} setSelectedOperatorObj={setSelectedOperatorObj}
        operatorValidityDate={operatorValidityDate} setOperatorValidityDate={setOperatorValidityDate} handleOperatorSelection={handleOperatorSelection}
      />

    </>
  );
};

export default WorkPermitWizard;
